import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Stack,
  Chip,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Grid,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useAuth } from '../services/AuthService';
import DocumentService from '../services/DocumentService';
import JobSeekerProfileService from '../services/JobSeekerProfileService';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import HubIcon from '@mui/icons-material/Hub';
import BadgeIcon from '@mui/icons-material/Badge';
import ConstructionIcon from '@mui/icons-material/Construction';
import HandymanIcon from '@mui/icons-material/Handyman';
import StarsIcon from '@mui/icons-material/Stars';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const StepItem = ({ title, description, completed, detail, profileItems = [], pendingColor, completedColor, icon: StepIcon, onAction }) => (
  <Paper 
    elevation={0}
    sx={{ 
      p: 3, 
      borderRadius: 3, 
      border: '1px solid', 
      borderColor: completed ? 'divider' : alpha(pendingColor, 0.5), 
      bgcolor: completed ? 'background.paper' : alpha(pendingColor, 0.06),
      transition: 'all 0.2s ease-in-out',
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        borderColor: completed ? completedColor : pendingColor,
        boxShadow: `0 8px 16px ${alpha(completed ? completedColor : pendingColor, 0.1)}`,
        transform: 'translateY(-2px)'
      },
      '&::before': !completed ? {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: 6,
        height: '100%',
        bgcolor: pendingColor
      } : {}
    }}
  >
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Box 
          sx={{ 
            width: 48, 
            height: 48, 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: completed ? alpha(completedColor, 0.1) : alpha(pendingColor, 0.1),
            color: completed ? completedColor : pendingColor
          }}
        >
          {completed ? <CheckCircleIcon /> : <StepIcon />}
        </Box>
      </Grid>
      <Grid item xs>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="h6" fontWeight={800} sx={{ textAlign: 'left', color: completed ? 'text.primary' : 'text.primary', lineHeight: 1.2 }}>{title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', mt: 0.5, fontWeight: completed ? 400 : 500 }}>{description}</Typography>
            </Box>
            <Box sx={{ ml: 1 }}>
              {completed ? (
                <Chip 
                  label="Completed" 
                  size="small"
                  sx={{ 
                    bgcolor: alpha(completedColor, 0.1), 
                    color: completedColor, 
                    fontWeight: 700,
                    border: `1px solid ${alpha(completedColor, 0.2)}`
                  }} 
                />
              ) : (
                <Chip 
                  label="Action Required" 
                  size="small"
                  sx={{ 
                    bgcolor: pendingColor, 
                    color: 'common.white', 
                    fontWeight: 800,
                    animation: 'pulse 2s infinite'
                  }} 
                />
              )}
            </Box>
          </Stack>
          
          {detail && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                textAlign: 'left', 
                p: 2, 
                bgcolor: completed ? 'action.hover' : 'background.paper', 
                borderRadius: 2, 
                border: '1px solid',
                borderColor: completed ? 'transparent' : alpha(pendingColor, 0.1),
                borderLeft: `4px solid ${completed ? completedColor : pendingColor}`,
                lineHeight: 1.6
              }}
            >
              {detail}
            </Typography>
          )}

          {profileItems && profileItems.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ mb: 1, textAlign: 'left', display: 'block', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, color: completed ? 'text.disabled' : 'text.primary' }}>
                {completed ? 'Verified profile info' : 'Missing or incomplete info'}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                {profileItems.map((p, i) => {
                  const isMissing = /^\s*No\b/i.test(p);
                  return (
                    <Chip
                        key={i}
                        label={p}
                        size="small"
                        icon={isMissing ? <InfoOutlinedIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                        color={isMissing ? 'error' : 'success'}
                        variant={isMissing ? 'filled' : 'filled'}
                        sx={(theme) => ({ 
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: isMissing ? 700 : 400,
                          ...(isMissing ? { 
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            color: 'error.dark',
                            border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`
                          } : {})
                        })}
                      />
                  );
                })}
              </Stack>
            </Box>
          )}

          {!completed && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button 
                variant="contained" 
                size="small" 
                onClick={onAction}
                sx={{ 
                  bgcolor: pendingColor, 
                  '&:hover': { bgcolor: alpha(pendingColor, 0.9) },
                  fontWeight: 700,
                  borderRadius: 1.5,
                  px: 3
                }}
              >
                Complete this step
              </Button>
            </Box>
          )}
        </Stack>
      </Grid>
    </Grid>
  </Paper>
);

const JobSeekerRoadmap = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [roadmapData, setRoadmapData] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (!user) return;
        const jobProfile = await new JobSeekerProfileService().getRecordsByUserId(user.sub);
        setProfile(jobProfile || {});
        if (jobProfile?.id) {
          const docs = await new DocumentService().getListOfDocumentsByProfileId(jobProfile.id);
          setDocuments(docs || []);
        }
        // Fetch server-side roadmap score/data
        try {
          const roadmap = await new JobSeekerProfileService().getMyRoadmap();
          setRoadmapData(roadmap);
        } catch (err) {
          console.warn('Failed to fetch roadmap data from API, falling back to client compute', err.message);
        }
      } catch (err) {
        console.error('Failed loading roadmap data', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const completedColor = theme.palette.success.main;
  const pendingColor = theme.palette.warning.main;

  const checks = roadmapData?.checks || {};
  const completedCount = roadmapData?.completedCount ?? 0;
  const score = roadmapData?.score ?? 0;
  const progressPercent = roadmapData?.progressPercent ?? 0;
  const missing = roadmapData?.missing || [];
  const profileItemsGetter = (key) => roadmapData?.profileItems?.[key] || [];

  // Statistics sourced from export/wa-messages/strategies.md
  const stats = {
    avgRate: 36.4,
    abnPercent: 46,
    ppePercent: 39.6,
    whiteCardPercent: 34.8,
    experiencePercent: 37.4,
    ticketPercent: 4.8,
    crossPostRatio: 8.85,
    peakHours: ['4:00 AM', '8:00 PM', '5:00 AM', '6:00 AM'],
    topLocations: ['Marrickville', 'Cammeray', 'Macquarie Park', 'Coogee']
  };

  const stepList = [
    { key: 'network', icon: HubIcon, title: 'Step 1: Master the Labourando Network', description: 'Have contact details updated, a bio, an avatar photo and active skills to apply quickly', completed: checks.network,
      detail: `Recruiters post most jobs during early-morning and evening peaks, so speed matters — a short ready-to-send bio and fast replies increase your chances.` },

    { key: 'paperwork', icon: BadgeIcon, title: 'Step 2: The Paperwork (Get Legal)', description: 'TFN / ABN and right to work evidence', completed: checks.paperwork,
      detail: `Approximately ${stats.abnPercent}% of job posts explicitly ask for an ABN. The platform average hourly rate is around $${stats.avgRate}/hr. Having your ABN and right-to-work documents makes you eligible for nearly half of the advertised roles.` },

    { key: 'whiteCard', icon: ConstructionIcon, title: 'Step 3: The Non-Negotiable (White Card)', description: 'General Construction Induction (White Card)', completed: checks.whiteCard,
      detail: `About ${stats.whiteCardPercent}% of unique job posts require a White Card. Employers commonly request it immediately — having it ready avoids disqualification and speeds hiring.` },

    { key: 'ppe', icon: HandymanIcon, title: 'Step 4: Gear Up (Essential PPE)', description: 'Steel-cap boots, hi-vis, hard hat, glasses', completed: checks.ppe,
      detail: `PPE is mentioned in roughly ${stats.ppePercent}% of job posts (boots, hi-vis and helmets are frequent). Not having basic PPE is a common reason workers miss early-morning shifts.` },

    { key: 'tickets', icon: StarsIcon, title: 'Step 5: Level Up Your Pay (Tickets)', description: 'Forklift, EWP, Traffic Control and similar tickets', completed: checks.tickets,
      detail: `Only ~${stats.ticketPercent}% of posts explicitly request tickets, but ticketed roles pay noticeably higher rates (specialised posts can reach $60/hr). Investing in one ticket often quickly increases your average hourly rate.` }
  ];

  const minimalInfo = !(profile && (profile?.labourRequirements?.length > 0 || documents.length > 0 || profile?.phone || profile?.abnNumber || (profile?.skills && profile.skills.length > 0)));

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {/* Modern Header Section */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 3, md: 5 }, 
          mb: 4, 
          borderRadius: 4, 
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.1),
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Back to Dashboard
            </Button>
          </Box>
          <Typography variant="h4" gutterBottom fontWeight={800} sx={{ color: 'primary.main', textAlign: 'left' }}>
            Labourando Success Journey
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, textAlign: 'left' }}>
            Track your progress and learn how to maximize your chances of getting hired. Higher scores lead to more frequent placements and better rates.
          </Typography>

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'background.paper', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.disabled', letterSpacing: 1 }}>
                  Your Profile Score
                </Typography>
                <Typography variant="h2" fontWeight={900} color="primary.main">
                  {score}<Typography component="span" variant="h5" color="text.disabled">/10</Typography>
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  {completedCount} of {stepList.length} steps complete
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Completion Progress</Typography>
                  <Typography variant="subtitle2" fontWeight={700} color="primary.main">{progressPercent}%</Typography>
                </Stack>
                <LinearProgress 
                  variant="determinate" 
                  value={progressPercent} 
                  sx={{ 
                    height: 12, 
                    borderRadius: 6,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 6,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`
                    }
                  }} 
                />
                
                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    disableElevation
                    onClick={() => navigate('/jobseeker/profile-management')}
                    sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 700, textTransform: 'none' }}
                  >
                    Update Profile
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={() => navigate('/jobseeker/profile-management')}
                    sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 700, textTransform: 'none' }}
                  >
                    Upload Documents
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {minimalInfo && (
        <Alert 
          severity="info" 
          sx={{ mb: 4, borderRadius: 2, border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.2) }}
          action={
            <Button size="small" color="inherit" onClick={() => navigate('/jobseeker/profile-management')} sx={{ fontWeight: 700 }}>
              Fix Now
            </Button>
          }
        >
          Your profile is currently missing key information. Complete your details to unlock a personalized roadmap.
        </Alert>
      )}

      <Stack spacing={2.5}>
        {stepList.map((s) => (
          <StepItem
            key={s.key}
            icon={s.icon}
            title={s.title}
            description={s.description}
            completed={s.completed}
            detail={s.detail}
            profileItems={profileItemsGetter(s.key)}
            pendingColor={pendingColor}
            completedColor={completedColor}
            onAction={() => navigate('/jobseeker/profile-management')}
          />
        ))}
      </Stack>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-start' }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Back to Dashboard
        </Button>
      </Box>
    </Container>
  );
};

export default JobSeekerRoadmap;
